/**
 * Export preferences and per-session publish state (W2-09 publish-import; DESIGN.md §4.16, §5.11).
 *
 *  - Export preferences are user-level (`settings.ext['publish.*']`): the last export target, where files go, and
 *    the package options (phiedit.json, info.txt, unused files, JSON style, the chart-only speed bake). Ctrl+E opens
 *    the Export dialog with exactly these "last settings"; the Publish workspace and the dialog both write them.
 *  - The "RPE version" override of Publish > Advanced applies to exports only and is never written into the project
 *    (ExportOptions.rpeVersionOverride: "for this export only"); it lives in memory per project for this session.
 *  - Field focus requests ("Edit chart details" from the palette or a checker fix) reach the package form.
 */

import type { SettingsService } from '../../app/settings/types';
import type { ProjectId } from '../../core/model/types';
import type { Json, Unsubscribe } from '../../core/util/types';

/** What the Export button writes (DESIGN.md §5.11: package, chart file only, project backup). */
export type PublishTarget = 'pez' | 'chartJson' | 'backup';
export const PUBLISH_TARGETS: readonly PublishTarget[] = ['pez', 'chartJson', 'backup'];

/** Where an exported file goes: the browser's downloads, or a file chosen with the save dialog (Chromium). */
export type Destination = 'download' | 'saveAs';
export const DESTINATIONS: readonly Destination[] = ['download', 'saveAs'];

export type FloatStyle = 'rpe' | 'shortest';
export const FLOAT_STYLES: readonly FloatStyle[] = ['rpe', 'shortest'];

export interface ExportPrefs {
  readonly target: PublishTarget;
  readonly destination: Destination;
  /**
   * Write phiedit.json (editor-only data, restored when the package is imported again). Backups always do; a player
   * package does not by default (UX-078: it can be larger than the chart, and Phira ignores it).
   */
  readonly includeSidecar: boolean;
  /** Write info.txt for RPE's importer (RPE-compatible profile only). */
  readonly includeInfoTxt: boolean;
  /** Also pack files nothing in the chart uses. Backups always do. */
  readonly includeUnusedAssets: boolean;
  /** Indented JSON instead of the compact form. */
  readonly pretty: boolean;
  readonly floatStyle: FloatStyle;
  /** Chart file only, RPE 1.7 speed projects: bake eased speed to linear pieces so a bare chart.json plays the same. */
  readonly bakeSpeedForChartOnly: boolean;
}

export const DEFAULT_PREFS: ExportPrefs = {
  target: 'pez',
  destination: 'download',
  includeSidecar: false,
  includeInfoTxt: true,
  includeUnusedAssets: false,
  pretty: false,
  floatStyle: 'rpe',
  bakeSpeedForChartOnly: true,
};

/** `settings.ext` keys of the preferences (Settings page rows use `ext.` + these). */
export const PREF_KEYS: Readonly<Record<keyof ExportPrefs, string>> = {
  target: 'publish.target',
  destination: 'publish.destination',
  includeSidecar: 'publish.includeSidecar',
  includeInfoTxt: 'publish.includeInfoTxt',
  includeUnusedAssets: 'publish.includeUnusedAssets',
  pretty: 'publish.pretty',
  floatStyle: 'publish.floatStyle',
  bakeSpeedForChartOnly: 'publish.bakeSpeedForChartOnly',
};

function choice<T extends string>(v: Json | undefined, options: readonly T[], fallback: T): T {
  return typeof v === 'string' && (options as readonly string[]).includes(v) ? (v as T) : fallback;
}

function flag(v: Json | undefined, fallback: boolean): boolean {
  return typeof v === 'boolean' ? v : fallback;
}

/** The current preferences (unset or invalid values fall back to the defaults). */
export function readPrefs(settings: Pick<SettingsService, 'getExt'>): ExportPrefs {
  const get = (k: keyof ExportPrefs): Json | undefined => settings.getExt(PREF_KEYS[k]);
  const d = DEFAULT_PREFS;
  return {
    target: choice(get('target'), PUBLISH_TARGETS, d.target),
    destination: choice(get('destination'), DESTINATIONS, d.destination),
    includeSidecar: flag(get('includeSidecar'), d.includeSidecar),
    includeInfoTxt: flag(get('includeInfoTxt'), d.includeInfoTxt),
    includeUnusedAssets: flag(get('includeUnusedAssets'), d.includeUnusedAssets),
    pretty: flag(get('pretty'), d.pretty),
    floatStyle: choice(get('floatStyle'), FLOAT_STYLES, d.floatStyle),
    bakeSpeedForChartOnly: flag(get('bakeSpeedForChartOnly'), d.bakeSpeedForChartOnly),
  };
}

export function writePref<K extends keyof ExportPrefs>(
  settings: Pick<SettingsService, 'setExt'>,
  key: K,
  value: ExportPrefs[K],
): void {
  settings.setExt(PREF_KEYS[key], value);
}

// ---------------------------------------------------------------------------------------------------------------
// Session state (not persisted, not part of the project)
// ---------------------------------------------------------------------------------------------------------------

/** Short names of the package form fields `publish.details` can focus. */
export type FocusField = 'name' | 'level' | 'charter' | 'preview' | 'tags';

/**
 * Document paths of the PACKAGE form's controls (each control's wrapper carries its path as `data-focus-field`):
 * what `publish.focusField` accepts.
 */
export const PACKAGE_FIELD_PATHS = [
  'meta.name',
  'meta.level',
  'meta.difficulty',
  'meta.charter',
  'meta.composer',
  'meta.illustrator',
  'meta.previewStart',
  'meta.previewEnd',
  'meta.tags',
  'meta.intro',
  'meta.tip',
] as const;
export type PackageFieldPath = (typeof PACKAGE_FIELD_PATHS)[number];

export function isPackageFieldPath(field: string): field is PackageFieldPath {
  return (PACKAGE_FIELD_PATHS as readonly string[]).includes(field);
}

/** The control path of a `publish.details` field. */
export function focusFieldPath(field: FocusField): PackageFieldPath {
  return field === 'preview' ? 'meta.previewStart' : `meta.${field}`;
}

class PublishSession {
  private readonly overrides = new Map<ProjectId, number>();
  private focus: { readonly field: PackageFieldPath; readonly nonce: number } | null = null;
  private nonce = 0;
  private readonly listeners = new Set<() => void>();
  private version = 0;

  /** Changes on every update (React subscriptions). */
  get revision(): number {
    return this.version;
  }

  /** RPE version written instead of the project's for exports of this project in this session (null = none). */
  rpeVersionOverride(project: ProjectId): number | null {
    return this.overrides.get(project) ?? null;
  }

  setRpeVersionOverride(project: ProjectId, value: number | null): void {
    if (value === null) this.overrides.delete(project);
    else this.overrides.set(project, value);
    this.notify();
  }

  /** The last focus request (consumed by the package form once). */
  get focusRequest(): { readonly field: PackageFieldPath; readonly nonce: number } | null {
    return this.focus;
  }

  requestFocus(field: PackageFieldPath): void {
    this.focus = { field, nonce: ++this.nonce };
    this.notify();
  }

  clearFocusRequest(nonce: number): void {
    if (this.focus?.nonce === nonce) this.focus = null;
  }

  subscribe(cb: () => void): Unsubscribe {
    this.listeners.add(cb);
    return () => {
      this.listeners.delete(cb);
    };
  }

  /** Forget everything (tests). */
  reset(): void {
    this.overrides.clear();
    this.focus = null;
    this.notify();
  }

  private notify(): void {
    this.version++;
    for (const cb of [...this.listeners]) cb();
  }
}

export const publishSession = new PublishSession();
